// Stripe API service for handling subscriptions
import { loadStripe } from '@stripe/stripe-js';
import { getMetaAttribution } from '../utils/metaAttribution.js';

// ---- SWITCH BETWEEN TEST AND PRODUCTION STRIPE KEYS ----
// Comment/uncomment the appropriate key to switch between test and production

// TEST Stripe publishable key
// const STRIPE_PUBLISHABLE_KEY = 'pk_test_51QHSHQCTqq6nTI6oTk8d5esvt2inBn6beKV8gYZRS3OOyrZcKe0qdmeG89flp8LuKqB47N4VZWGl3JjWeuGH1dxh00qe3gAbRH';

// PRODUCTION Stripe publishable key - uncomment to use production
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51QHSHQCTqq6nTI6o0uOo2Ea6OO3zoYjFGZgY1JANteUTMdYzpWJPSwAG1NzKIv1ecTQFBSdUBMXyknxkP9iY3wXN00MXSTtWFX';

// Lambda API endpoint - simplified to just the base endpoint
const LAMBDA_ENDPOINT = 'https://kzh81dmshh.execute-api.us-east-1.amazonaws.com/handle-stripe';

// User subscription Lambda endpoints
const USER_SUBSCRIBE_ENDPOINT = 'https://yj7bu00r8f.execute-api.us-east-1.amazonaws.com/user-subscribe';
const USER_UNSUBSCRIBE_ENDPOINT = 'https://yj7bu00r8f.execute-api.us-east-1.amazonaws.com/user-unsubscribe';

// Initialize Stripe
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

/**
 * Create a subscription for a user
 * @param {string} customerEmail - Customer email for Stripe customer
 * @param {string} userId - User ID for reference
 * @param {string} priceId - Stripe Price ID for the selected plan
 * @param {Object} metadata - Additional metadata to pass to Stripe
 * @param {number} trialDays - Number of trial days (default 0)
 * @returns {Promise} - Stripe checkout session
 */
export const createSubscription = async (customerEmail, userId, priceId, metadata = {}, trialDays = 0) => {
  try {
    const { fbp, fbc, source_url } = getMetaAttribution();
    // Call the Lambda endpoint with operation as query parameter
    const response = await fetch(`${LAMBDA_ENDPOINT}?operation=create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerEmail,
        userId,
        priceId,
        trialDays, // Add trial days to the request
        metadata: {
          userId: userId, // Ensure userId is in metadata (Lambda already handles this)
          ...metadata // Allow additional metadata from Join.jsx
        },
        fbp,
        fbc,
        source_url,
        successUrl: `${window.location.origin}/paymentsuccess?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/subscribe`
      })
    });

    const result = await response.json();
    
    if (result.error) {
      throw new Error(result.error);
    }

    const stripe = await getStripe();
    return stripe.redirectToCheckout({ sessionId: result.sessionId });
  } catch (error) {
    () => {};
    throw new Error(`Failed to create subscription: ${error.message}`);
  }
};

/**
 * Get customer subscriptions
 * @param {string} customerId - Stripe customer ID
 * @returns {Promise} - Customer subscriptions
 */
export const getSubscriptions = async (customerId) => {
  try {
    const response = await fetch(`${LAMBDA_ENDPOINT}?operation=get-subscriptions&customerId=${customerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return await response.json();
  } catch (error) {
    () => {};
    throw new Error(`Failed to get subscriptions: ${error.message}`);
  }
};

/**
 * Cancel a subscription
 * @param {string} subscriptionId - Stripe subscription ID
 * @returns {Promise} - Cancelled subscription
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    const response = await fetch(`${LAMBDA_ENDPOINT}?operation=cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        subscriptionId
      })
    });

    const result = await response.json();
    
    if (result.error) {
      throw new Error(result.error);
    }
    
    return result;
  } catch (error) {
    () => {};
    throw new Error(`Failed to cancel subscription: ${error.message}`);
  }
};

/**
 * Get customer by email
 * @param {string} email - Customer email
 * @returns {Promise} - Customer data
 */
export const getCustomerByEmail = async (email) => {
  try {
    const response = await fetch(`${LAMBDA_ENDPOINT}?operation=get-customer&email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = await response.json();
    return result;
  } catch (error) {
    () => {};
    throw new Error(`Failed to get customer: ${error.message}`);
  }
};

/**
 * Create customer if not exists
 * @param {string} email - Customer email
 * @param {string} userId - User ID for reference
 * @returns {Promise} - Customer data
 */
export const createCustomer = async (email, userId) => {
  try {
    const existingCustomer = await getCustomerByEmail(email);
    if (existingCustomer) return existingCustomer;

    const response = await fetch(`${LAMBDA_ENDPOINT}?operation=create-customer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        userId
      })
    });

    return await response.json();
  } catch (error) {
    () => {};
    throw new Error(`Failed to create customer: ${error.message}`);
  }
};

/**
 * Helper function to create a proxy URL with parameters
 * @param {string} baseUrl - Base URL for the service
 * @param {Object} params - Parameters to add as query params
 * @returns {string} - URL with query parameters
 */
const createProxyUrl = (baseUrl, params) => {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });
  return url.toString();
};

/**
 * Update user subscription status in DynamoDB to 'subscribed'
 * @param {string} userId - User ID to update
 * @returns {Promise} - Updated user data
 */
export const updateUserSubscribed = async (userId) => {
  try {
    console.log(`Activating subscription status for userId=${userId}`);
    
    // Use simple POST request with minimal headers
    const response = await fetch(USER_SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        updateSubscriptionStatus: true // Explicitly set this to true to update subscription status
      })
    });

    // Add short timeout to ensure response is processed properly
    await new Promise(resolve => setTimeout(resolve, 300));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    // Simple fallback if API call fails
    return {
      message: 'Subscription status update simulated',
      userId,
      subscriptionStatus: 'subscribed',
      warning: 'Database update may be pending'
    };
  }
};

/**
 * Update user subscription status in DynamoDB to 'inactive'
 * @param {string} userId - User ID to update
 * @param {string} subscriptionEndDate - Date when subscription ends
 * @returns {Promise} - Updated user data
 */
export const updateUserUnsubscribed = async (userId, subscriptionEndDate) => {
  try {
    // Use simple POST request with minimal headers
    const response = await fetch(USER_UNSUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        subscriptionEndDate
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    // Simple fallback if API call fails
    return {
      message: 'Subscription status update simulated',
      userId,
      subscriptionStatus: 'inactive',
      subscriptionEndDate,
      warning: 'Database update may be pending'
    };
  }
};

/**
 * Update user plan type in DynamoDB
 * @param {string} userId - User ID to update
 * @param {string} planType - Plan type (contributor, member, screamerplus)
 * @returns {Promise} - Updated user data
 */
export const updateUserPlanType = async (userId, planType) => {
  try {
    console.log(`Updating plan type for userId=${userId} to planType=${planType}`);
    
    // Use simple POST request with minimal headers - same approach as updateUserSubscribed
    const response = await fetch(USER_SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        planType,
        updateSubscriptionStatus: false // Don't update subscription status
      })
    });

    // Add short timeout to ensure response is processed properly
    await new Promise(resolve => setTimeout(resolve, 300));

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating plan type:', error);
    // Simple fallback if API call fails
    return {
      message: 'Plan type update simulated',
      userId,
      planType,
      warning: 'Database update may be pending'
    };
  }
}; 